import { IHttpClient } from "../../../lib/http";
import { useMemo } from "react";
import { AutomaticChargeEntity, AutomaticChargesDTO } from "./automaticCharges.dto";
import { dtoToEntity } from "./automaticCharges.mapper";

export interface AutomaticChargeRequest {
  estimatedCollectDateTo?: string;
  estimatedCollectDateFrom?: string;
  jobLastExecutionFrom?: string;
  jobLastExecutionTo?: string;
  bookingId?: string;
  multipleBookingId?: string;
  currency?: string;
  paymentType?: string;
  amount?: number | undefined;
  chargeStatus?: string;
}

export interface GetAutomaticChargeRequest {
  request: AutomaticChargeRequest;
  size: number;
  page: number;
}

export interface CollectAutomaticChargeRequest {
  id: string;
}

interface AutomaticChargeResponse {
  data: AutomaticChargesDTO[];
  pagination: {
    hasNextPage: boolean;
    page: number;
    size: number;
    total: number;
  };
}

interface AutomaticChargeListResponse {
  content: AutomaticChargeEntity[];
  pagination: {
    hasNextPage: boolean;
    page: number;
    size: number;
    total: number;
  };
}

interface AutomaticChargeRequestBody {
  params: AutomaticChargeRequest & { propertyTicker: string };
  pagination: {
    size: number;
    page: number;
  };
}

export interface AutomaticChargeListColumns {
  propertyTicker: string;
}

export const useAutomaticChargesApi = (httpClient: IHttpClient) => {
  return useMemo(
    () => ({
      getAutomaticCharges: (props: GetAutomaticChargeRequest & AutomaticChargeListColumns) =>
        getAutomaticCharges(httpClient, props),
      getCollectAutomaticCharges: (props: CollectAutomaticChargeRequest) =>
        getCollectAutomaticCharges(httpClient, props),
      cancelAutomaticCharge: (props: CollectAutomaticChargeRequest) =>
        cancelAutomaticCharge(httpClient, props),
    }),
    [httpClient]
  );
};

async function getAutomaticCharges(
  httpClient: IHttpClient,
  props: GetAutomaticChargeRequest & AutomaticChargeListColumns
): Promise<AutomaticChargeListResponse> {
  const { size, page } = props;
  const { data, ...rest } = await httpClient.post<
    AutomaticChargeRequestBody,
    AutomaticChargeResponse
  >(`/automatic-charges/find`, {
    params: {
      propertyTicker: props.propertyTicker,
      ...props.request,
    },
    pagination: {
      size,
      page: page + 1,
    },
  });
  return { content: dtoToEntity(data) || [], ...rest };
}

async function getCollectAutomaticCharges(
  httpClient: IHttpClient,
  { id }: CollectAutomaticChargeRequest
) {
  return await httpClient.get(`/automatic-charges/${id}/collect`);
}

async function cancelAutomaticCharge(
  httpClient: IHttpClient,
  { id }: CollectAutomaticChargeRequest
) {
  return await httpClient.delete(`/automatic-charges/${id}`);
}
